import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, UserPlus, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Empty, NativeSelect, StatusPill } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, inr } from "@/lib/format";

const LABEL = { pending_payment: "Awaiting payment", active: "Member", rejected: "Rejected", activating: "Processing" };
const TONE = { pending_payment: "pending", active: "confirmed", rejected: "skipped", activating: "pending" };

function ApproveDialog({ reg, onClose, onDone }) {
  const [mode, setMode] = useState("cash");
  const approve = async () => {
    try { await api.post(`/registrations/${reg.id}/approve`, { mode }); toast.success(`${reg.name} added as a customer`); onClose(); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!reg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="approve-dialog">
        <DialogHeader><DialogTitle>Mark {inr(reg?.fee)} received from {reg?.name}</DialogTitle></DialogHeader>
        <div className="text-sm text-muted-foreground">This creates the customer, who can then order from their personal link.</div>
        <NativeSelect value={mode} onChange={(e) => setMode(e.target.value)} data-testid="approve-mode-select">
          <option value="cash">Cash</option><option value="upi">UPI</option><option value="bank">Bank transfer</option>
        </NativeSelect>
        <DialogFooter><Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={approve} data-testid="approve-confirm-btn">Mark paid & add customer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Signups() {
  const [rows, setRows] = useState([]);
  const [approve, setApprove] = useState(null);
  const load = () => api.get("/registrations").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const joinUrl = `${window.location.origin}/join`;
  const reject = async (r) => {
    try { await api.post(`/registrations/${r.id}/reject`); toast.success("Sign-up rejected"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="signups-page">
      <PageHeader title="Member sign-ups" subtitle="New customers who joined from your website link.">
        <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(joinUrl); toast.success("Join link copied. Add it to nexfresh.in"); }} data-testid="copy-join-link-btn">
          <Copy className="mr-2 h-4 w-4" />Copy join link
        </Button>
      </PageHeader>
      <div className="mb-6 rounded-2xl border border-dashed border-[#D9D2C0] bg-white/70 p-4 text-sm" data-testid="join-link-box">
        Put this link on nexfresh.in as the "Register" button: <code className="font-mono text-[#0F382C]" data-testid="join-link">{joinUrl}</code>
      </div>
      {rows.length === 0 ? <Empty icon={UserPlus} title="No sign-ups yet" text="Share the join link on your website and WhatsApp status." /> : (
        <div className="rounded-2xl border border-[#E7E2D6] bg-white divide-y">
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-4" data-testid={`signup-row-${r.id}`}>
              <div>
                <div className="font-semibold">{r.name} <span className="text-sm text-muted-foreground font-normal">· {r.phone}</span></div>
                <div className="text-xs text-muted-foreground">{[r.area, r.address].filter(Boolean).join(" · ") || "No address"} · joined {fmtDateTime(r.created_at)}</div>
                {r.notes && <div className="text-xs mt-1">{r.notes}</div>}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{inr(r.fee)}</span>
                <StatusPill status={TONE[r.status]} label={LABEL[r.status] || r.status} testid={`signup-status-${r.id}`} />
                {r.status === "pending_payment" && <>
                  <Button size="sm" className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => setApprove(r)} data-testid={`signup-approve-${r.id}`}><Check className="mr-1 h-4 w-4" />Mark paid</Button>
                  <Button size="sm" variant="ghost" onClick={() => reject(r)} data-testid={`signup-reject-${r.id}`}><X className="h-4 w-4" /></Button>
                </>}
                {r.customer_id && <Button size="sm" variant="outline" asChild><Link to={`/customers/${r.customer_id}`} data-testid={`signup-customer-${r.id}`}>Open customer</Link></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <ApproveDialog reg={approve} onClose={() => setApprove(null)} onDone={load} />
    </div>
  );
}
